<template>
  <div>
    <h3>{{ pageHeading }}</h3>

    <div v-if="isLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>
    <template v-else>
      <div class="custom-search d-flex justify-content-between align-items-center mb-1">
        <UnitsSearchFilters
          :filter-category-id="filterCategoryId"
          :filter-stock-location-id="filterStockLocationId"
          :filter-stock-below="filterStockBelow"
          :filter-keyword="filterKeyword"
          :category-filter-options="categoryFilterOptions"
          :stock-location-filter-options="stockLocationFilterOptions"
          :company-purpose="companyPurpose"
          :show-barcode-target="$route.name === 'products'"
          :has-active-filters="hasActiveFilters"
          :is-table-loading="isTableLoading"
          :filtered-count="unitsTotal"
          :disabled="isTableLoading"
          @update:filter-category-id="onFilterCategoryChange"
          @update:filter-stock-location-id="onFilterStockLocationChange"
          @update:filter-stock-below="filterStockBelow = $event"
          @update:filter-keyword="filterKeyword = $event"
          @reset="resetFilters"
        />
        <b-form-group
          v-if="canCreateProducts"
          class="mb-0"
        >
          <b-button
              variant="primary"
              class="mx-1"
              size="sm"
              @click="openUnitModal(null)"
          >
            <span> Add {{ companyPurpose !== 'rent' ? 'Product' : 'Unit' }} </span>
          </b-button>
        </b-form-group>
      </div>
      <vue-good-table
          v-if="unitesTableView"
          mode="remote"
          :columns="displayColumns"
          :rows="units"
          :total-rows="unitsTotal"
          :search-options="{
          enabled: false,
          externalQuery: searchTerm }"
          :pagination-options="{
            enabled: true,
            perPage: pageLength,
            perPageDropdownEnabled: false,
          }"
          @page-change="onUnitsPageChange"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'picture'">
            <img
                :src="props.row.picture[0].src"
                class="table-row-thumb"
                alt=""
            />
          </span>
          <span v-else-if="props.column.field === 'name'">
            <DatatableUserText
              field="name"
              :value="props.row.name"
            />
          </span>
          <span v-else-if="props.column.field === 'total_stock'">
            <template v-if="props.row.stock_management">
              <span
                class="products-qty"
                :class="qtyClass(unitTotalStock(props.row))"
              >
                {{ unitTotalStock(props.row) }}
              </span>
            </template>
          </span>
          <span v-else-if="props.column.field === 'action'" class="action-link-group products-action">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openUnitModal(props.row)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>{{ canChangeProducts ? 'View / Edit' : 'View' }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="canChangeProducts"
                :disabled="!props.row.stock_management"
                @click="openStockChangesModal(props.row)"
              >
                <feather-icon icon="PackageIcon" class="mr-50" />
                <span>Manage Stock</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="canChangeProducts"
                @click="deleteUnit(props.row)"
              >
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <span v-else-if="props.column.field === 'description'">
            <DatatableUserText
              field="description"
              :value="$stripHtml(props.row.description)"
            />
          </span>
          <span v-else-if="props.column.field === 'price'">
            {{ formatUnitPrice(props.row.price) }}
          </span>
          <span v-else-if="props.column.field === 'platform'" class="text-center">
            <div v-if="props.row.platform ?? false" class="text-center">
              <img v-if="props.row.platform.picture ?? false"
                  :src="props.row.platform.picture"
                  :style="{ width: '50px' }"
              />
              <div>
                <small>
                  <a target="_blank" :href="props.row.platform.url">{{ props.row.platform.name }}<br/>{{ props.row.platform.type }}</a>
                </small>
              </div>
            </div>
            <div v-else-if="props.row.connected_platforms ?? false">
                <ul class="connected-platforms-list">
                  <li v-for="connectedPlatform in props.row.connected_platforms" class="d-flex align-items-center">
                    <img v-if="connectedPlatform.picture ?? false"
                         :src="connectedPlatform.picture"
                    />
                    <a target="_blank" :href="connectedPlatform.url + '/product/' + connectedPlatform.pivot.slug">{{ connectedPlatform.name }} ({{ connectedPlatform.type }})</a>
                  </li>
                </ul>
            </div>
          </span>
          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <!-- pagination -->
        <template #pagination-bottom="props">
          <TablePagination
              :value="tablePage"
              :total-rows="unitsTotal"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="(n) => { pageLength = n; tablePage = 1; props.perPageChanged({ currentPerPage: n }); fetchUnits() }"
            />
        </template>
      </vue-good-table>

      <b-card-group deck class="justify-content-left" v-if="!unitesTableView">
        <b-card
            v-for="unit in units"
            :key="unit.id"
            no-body
            :class="'unit-block mb-2'"
            :img-src="unit.picture[0].src"
            img-alt="Image"
            img-top
        >
          <b-card-body>
            <b-card-title class="text-primary">{{ unit.name }}</b-card-title>
            <b-card-text>{{ unit.description }}</b-card-text>
            <b-card-text><strong>Price:</strong> {{ unit.price }}</b-card-text>
            <b-card-text v-if="companyPurpose !== 'rent'">
              <strong>SKU:</strong> {{ unit.sku }}
            </b-card-text>
            <b-card-text v-if="unit.items_quantity != null && unit.items_quantity > 1">
              <strong>Items Contains:</strong> {{ unit.items_quantity }}
            </b-card-text>
          </b-card-body>

          <b-card-body class="d-flex flex-column-reverse">
            <b-button
                v-b-modal.edit-modal
                block
                variant="success"
                @click="openUnitModal(unit)"
            >
              <span class="text-light">{{ canChangeProducts ? 'View / Edit' : 'View' }}</span>
            </b-button>
          </b-card-body>
        </b-card>
      </b-card-group>

    </template>
    <ManageUnit
        :clickDate="clickDate"
        :unit="unit"
    />
    <StockChanges
      :clickDate="stockChangesClickDate"
      :unit="stockChangesUnit"
      @stock-updated="onStockUpdated"
    />
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import useVuelidate from '@vuelidate/core';
import { required, numeric, integer, minValue, minLength, email, requiredIf, withMessage } from '@vuelidate/validators';
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCardTitle,
  BCardText,
  BListGroup,
  BListGroupItem,
  BFormDatepicker,
  BButton,
  BModal,
  BFormGroup,
  BFormInput,
  BFormFile,
  BForm,
  BCardGroup,
  BSpinner,
  BFormTextarea, BFormCheckbox, BPagination, BImg,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import { toastSuccess, toastFailure } from '@/libs/toastification'
import {$themeConfig} from "@themeConfig";
import ManageUnit from "@/views/Components/ManageUnit.vue";
import StockChanges from "@/views/Components/StockChanges.vue";
import UnitsSearchFilters from "@/views/Components/UnitsSearchFilters.vue";
import { buildHierarchicalCategoryOptions } from '@/libs/categoryFilterOptions';
import { canStaffCreate, canStaffChange } from '@/libs/staffPermissions';
import { moneyRound } from '@/libs/utils';
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'
import DatatableUserText from '@/views/Components/Elements/DatatableUserText.vue'

export default {
  components: {
    TablePagination,
    ManageUnit,
    StockChanges,
    UnitsSearchFilters,
    DatatableCellValue,
    DatatableUserText,
    BImg,
    BPagination,
    BRow,
    BCol,
    BFormCheckbox,
    BCard,
    BCardBody,
    BCardTitle,
    BCardText,
    BListGroup,
    BListGroupItem,
    BFormDatepicker,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    BFormFile,
    BForm,
    BCardGroup,
    BSpinner,
    BFormTextarea,
    BDropdown,
    BDropdownItem,
    VueGoodTable
  },
  data() {

    const { noUnitImage } = $themeConfig.app;

    return {
      unit: null,
      clickDate: null,
      stockChangesUnit: null,
      stockChangesClickDate: null,
      noUnitImage,
      unitesTableView: true,
      minDate: new Date(),
      isLoading: true,
      edited: {
        id: 0,
        name: null,
        description: null,
        picture: null,
        maintenance_from: null,
        maintenance_to: null,
        company_id: window.companyId > 0 ? window.companyId : null,
        company: {},
        sku: null,
        price: null,
        items_quantity: 1
      },
      temporaryPicture: null,
      isEditLoading: false,
      companyPurpose: window.companyPurpose,
      companyId: window.companyId,
      isTableLoading: true,
      filterCategoryId: null,
      filterStockLocationId: null,
      filterStockBelow: '',
      filterKeyword: '',
      filterTimer: null,
      skipFilterWatch: false,
      searchTerm: '',
      columns: [
        {
          label: 'Picture',
          field: 'picture',
          thClass: 'table-picture-col',
          tdClass: 'table-picture-col',
          sortable: false
        },
        {
          label: 'Name',
          field: 'name',
        },
        {
          label: 'Description',
          field: 'description',
        },
        {
          label: 'Price',
          field: 'price',
          width: '100px',
        },
        {
          label: 'Qty',
          field: 'total_stock',
          width: '70px',
          type: 'number',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'Platform',
          field: 'platform',
          width: '100px',
        },
        {
          label: 'Actions',
          field: 'action',
          thClass: 'text-center products-action-col',
          tdClass: 'text-center products-action-col',
          sortable: false,
        },
      ],
      pageLength: 25,
      tablePage: 1,
    }
  },
  computed: {
    ...mapGetters({
      units: 'units/units',
      unitsTotal: 'units/unitsTotal',
      companySettings: 'companies/companySettings',
      unitCategories: 'unitCategories/unitCategories',
      stockLocations: 'stockLocations/stockLocations',
      authUser: 'auth/user',
    }),
    canCreateProducts() {
      return canStaffCreate(this.authUser, 'products')
    },
    canChangeProducts() {
      return canStaffChange(this.authUser, 'products')
    },
    pageHeading() {
      return this.companyPurpose !== 'rent' ? 'Products' : 'Units'
    },
    hasConnectedPlatform() {
      return !!(this.companySettings && this.companySettings.has_connected_platform)
    },
    displayColumns() {
      let columns = this.columns
      if (this.companyPurpose === 'rent') {
        columns = columns.filter(column => column.field !== 'total_stock')
      }
      if (!this.hasConnectedPlatform) {
        columns = columns.filter(column => column.field !== 'platform')
      }
      return columns
    },
    categoryFilterOptions() {
      return buildHierarchicalCategoryOptions(this.unitCategories, {
        allOption: { id: null, name: 'All categories' },
      })
    },
    stockLocationFilterOptions() {
      const locations = (this.stockLocations || [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))

      return [
        { id: null, name: 'All stock locations', picture: null },
        ...locations,
      ]
    },
    hasActiveFilters() {
      return this.filterCategoryId != null
        || this.filterStockLocationId != null
        || String(this.filterStockBelow || '').trim() !== ''
        || String(this.filterKeyword || '').trim() !== ''
    },
  },
  watch: {
    filterStockBelow() {
      if (this.skipFilterWatch) {
        return
      }
      if (this.filterTimer) {
        clearTimeout(this.filterTimer)
      }

      this.filterTimer = setTimeout(() => {
        this.tablePage = 1
        this.fetchUnits()
      }, 300)
    },
    filterKeyword() {
      if (this.skipFilterWatch) {
        return
      }
      if (this.filterTimer) {
        clearTimeout(this.filterTimer)
      }

      this.filterTimer = setTimeout(() => {
        this.tablePage = 1
        this.fetchUnits()
      }, 300)
    },
  },
  async mounted() {

    await this.loadCompanySettings().catch(() => {})
    if (this.companyPurpose !== 'rent') {
      await Promise.all([
        this.loadUnitCategories().catch(() => {}),
        this.loadStockLocations().catch(() => {}),
      ])
    }
    await this.fetchUnits()
    this.isLoading = false
  },
  validations() {

    let validateObj = {
      name: { required, minLength: minLength(3) },
      price: { required, numeric }
    };

    if (companyId === 0) {
      validateObj.company_id = { required, integer, minValue: minValue(1) };
      validateObj.company = { required };
    }

    if (companyPurpose !== 'rent') {
      //validateObj.sku = { required, minLength: minLength(3) };
    }

    validateObj.items_quantity = { integer, minValue: minValue(1) };

    return {
      edited:validateObj
    }
  },
  setup() {
    return { v$: useVuelidate() };
  },
  methods: {
    ...mapActions({
      loadUnits: 'units/loadUnits',
      updateUnit: 'units/updateUnit',
      addUnit: 'units/addUnit',
      deleteUnitAction: 'units/deleteUnit',
      loadCompanySettings: 'companies/loadCompanySettings',
      loadUnitCategories: 'unitCategories/loadUnitCategories',
      loadStockLocations: 'stockLocations/loadStockLocations',
    }),
    fetchUnits() {
      this.isTableLoading = true
      const filters = {
        page: this.tablePage,
        per_page: this.pageLength,
      }
      if (this.filterCategoryId) {
        filters.unit_category_id = this.filterCategoryId
      }
      if (this.filterStockLocationId) {
        filters.stock_location_id = this.filterStockLocationId
      }
      const stockBelow = this.parseFilterStockBelow(this.filterStockBelow)
      if (stockBelow != null) {
        filters.stock_below = stockBelow
      }
      const keyword = String(this.filterKeyword || '').trim()
      if (keyword !== '') {
        filters.keyword = keyword
      }

      return this.loadUnits(filters)
        .then((result) => {
          const total = Number(result?.total ?? 0)
          const maxPage = Math.max(1, Math.ceil(total / this.pageLength) || 1)
          if (this.tablePage > maxPage) {
            this.tablePage = maxPage
            return this.fetchUnits()
          }
          return result
        })
        .catch(() => {
          toastFailure('Can not load units')
        })
        .finally(() => {
          this.isTableLoading = false
        })
    },
    onUnitsPageChange(params) {
      this.tablePage = params.currentPage
      this.fetchUnits()
    },
    onFilterCategoryChange(value) {
      this.filterCategoryId = value
      if (this.skipFilterWatch) {
        return
      }
      this.tablePage = 1
      this.fetchUnits()
    },
    onFilterStockLocationChange(value) {
      this.filterStockLocationId = value
      if (this.skipFilterWatch) {
        return
      }
      this.tablePage = 1
      this.fetchUnits()
    },
    parseFilterStockBelow(value) {
      const trimmed = String(value ?? '').trim()
      if (trimmed === '') {
        return null
      }

      if (!/^\d+$/.test(trimmed)) {
        return null
      }

      return Number.parseInt(trimmed, 10)
    },
    resetFilters() {
      if (!this.hasActiveFilters) {
        return
      }

      if (this.filterTimer) {
        clearTimeout(this.filterTimer)
        this.filterTimer = null
      }

      this.skipFilterWatch = true
      this.filterCategoryId = null
      this.filterStockLocationId = null
      this.filterStockBelow = ''
      this.filterKeyword = ''
      this.tablePage = 1
      this.$nextTick(() => {
        this.skipFilterWatch = false
        this.fetchUnits()
      })
    },
    onContext(ctx) {
      this.formatted = ctx.selectedFormatted;
      this.selected = ctx.selectedYMD;
    },
    openUnitModal(row) {
      this.unit = row;
      this.clickDate = new Date();
    },
    openStockChangesModal(row) {
      this.stockChangesUnit = row;
      this.stockChangesClickDate = new Date();
    },
    unitTotalStock(unit) {
      return Number(unit?.total_stock ?? 0)
    },
    qtyClass(qty) {
      const value = Number(qty)
      if (value < 0) {
        return 'products-qty--low'
      }
      if (value > 0 && value < 10) {
        return 'products-qty--warn'
      }
      return ''
    },
    formatUnitPrice(price) {
      const symbol = this.companySettings?.currency_symbol || window.currency_symbol || '$'
      return `${symbol}${moneyRound(price).toFixed(2)}`
    },
    onStockUpdated({ unitId, totalStock }) {
      const unit = this.units.find(u => u.id === unitId);
      if (unit) {
        unit.total_stock = totalStock;
      }
    },
    getValidationErrors() {
      const errors = [];

      // Access the validation states
      const fields = this.v$.edited;

      for (const field in fields) {
        if (fields[field].$error) {
          // Check if the field has an error and is not pending
          if (fields[field].$pending) continue; // Skip pending validations

          // Generate error messages based on the specific validation rules
          if (fields[field].required && !this.edited[field]) {
            errors.push(`${field} is required.`);
          }
          if (fields[field].minLength && this.edited[field]?.length < 3) {
            errors.push(`${field} must be at least 3 characters long.`);
          }
          // Add more validations and corresponding messages as needed
          // Example for numeric validation:
          if (fields[field].numeric && isNaN(this.edited[field])) {
            errors.push(`${field} must be a valid number.`);
          }
        }
      }
      return errors;
    },
    getTemporaryPicture() {
      return new Promise(resolve => {
        const reader = new FileReader()

        reader.addEventListener('load', () => {
          this.temporaryPicture = reader.result
          resolve()
        }, false)

        if (/\.(jpe?g|png|gif)$/i.test(this.edited.picture.name)) {
          reader.readAsDataURL(this.edited.picture)
        }
      })
    },
    confirmationMessage(message) {
      return this.$bvModal.msgBoxConfirm(message, {
        title: 'Please Confirm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'No',
      })
    },
    async deleteUnit(unit) {
      const message = `Are you sure you want to delete "${unit.name}"? This cannot be undone.`
      if (!await this.confirmationMessage(message)) {
        return
      }

      this.deleteUnitAction(unit.id)
        .then(() => {
          toastSuccess('Product has been deleted.')
        })
        .catch((error) => {
          const data = error?.response?.data
          const apiMessage = data?.error?.[0] || data?.message
          toastFailure(apiMessage || 'Failed to delete the product.')
        })
    },
  },
}
</script>

<style scoped>
.products-qty {
  display: inline-block;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}

.products-qty--low {
  color: #ea5455;
}

.products-qty--warn {
  color: #ff9f43;
}

.products-action-col {
  width: 72px !important;
  min-width: 72px !important;
  max-width: 72px !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

.products-action {
  display: inline-flex;
  justify-content: center;
  width: auto;
}

.products-action :deep(.dropdown-toggle) {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
</style>
